// Timeline types
export enum StageStatus {
  COMPLETE = 'COMPLETE',
  IN_PROGRESS = 'IN_PROGRESS',
  LOCKED = 'LOCKED'
}

export enum StageType {
  // Google / Bing: Search Engine Discovery
  SITE_VERIFIED = 'SITE_VERIFIED',
  PAGES_INDEXED = 'PAGES_INDEXED',
  CRAWL_STABILIZED = 'CRAWL_STABILIZED',
  SEARCH_VISIBILITY = 'SEARCH_VISIBILITY',
  CTR_SIGNALS = 'CTR_SIGNALS',
  CLICK_PATTERNS = 'CLICK_PATTERNS',
  GROWTH_ESTABLISHED = 'GROWTH_ESTABLISHED',
  OPTIMIZATION_READY = 'OPTIMIZATION_READY',
  // Legacy GA4 visitor journey values retained for backward-compatible parsing
  ANALYTICS_CONNECTED = 'ANALYTICS_CONNECTED',
  VISITOR_TRACKING_ACTIVE = 'VISITOR_TRACKING_ACTIVE',
  ENGAGEMENT_SIGNALS = 'ENGAGEMENT_SIGNALS',
  RETURNING_VISITORS = 'RETURNING_VISITORS',
  CONVERSION_TRACKING = 'CONVERSION_TRACKING',
  // Legacy (backward compat)
  QUERY_IMPRESSIONS = 'QUERY_IMPRESSIONS',
  CONVERSION_DATA = 'CONVERSION_DATA',
  EXPERIMENTS_READY = 'EXPERIMENTS_READY',
}

export interface MaturityStage {
  stageType: StageType;
  stageName: string;
  description: string;
  status: StageStatus;
  completedDate: string | null;
  estimatedDaysRemaining: number;
  progressPercent: number;
  unlockCondition: string;
  whyItMatters: string;
}

export interface FeatureUnlock {
  featureName: string;
  description: string;
  daysUntilUnlock: number;
  unlockCondition: string;
}

export interface TimelineData {
  siteAgeDays: number;
  currentStage: string;
  overallProgressPercent: number;
  nextMilestone: string;
  daysUntilNextMilestone: number | null;
  stages: MaturityStage[];
  unlockedFeatures: string[];
  upcomingFeatures: FeatureUnlock[];
  
  // Engine-specific timelines
  engineTimelines: EngineTimelines;
}

export interface EngineTimelines {
  googleTimeline: EngineTimeline;
  bingTimeline: EngineTimeline;
  availableEngines: string[];
  recommendedNextEngine: string | null;
}

export interface EngineTimeline {
  engine: string; // "GOOGLE", "BING"
  engineName: string; // "Google Search Console", "Bing Webmaster Tools"
  journeyName: string; // "Search Engine Discovery"
  isConnected: boolean;
  hasData: boolean;
  dataDays: number; // Days of data available
  status: string; // "ACTIVE", "CONNECTED_NO_DATA", "NOT_CONNECTED"
  stages: MaturityStage[];
  nextMilestone: string;
  daysUntilNextMilestone: number;
  progressPercent: number;
  unlockedFeatures: string[];
  setupInstructions: string | null; // How to connect this engine
  
  // Enhanced indexing status for early stage owners
  indexingStatus: PageIndexingStatus;
}

export interface PageIndexingStatus {
  // Overall indexing metrics
  totalPages: number; // Total pages discovered on site
  indexedPages: number; // Pages successfully indexed
  pendingPages: number; // Pages submitted but not indexed yet
  excludedPages: number; // Pages excluded from indexing (robots.txt, noindex, etc.)
  indexingRate: number; // Percentage of pages indexed (indexedPages/totalPages * 100)
  
  // Recent activity (last 7 days)
  recentlyIndexed: number; // Pages indexed in last 7 days
  recentlySubmitted: number; // Pages submitted in last 7 days
  indexingErrors: number; // Pages with indexing errors
  
  // Indexing health insights
  indexingHealth: string; // "EXCELLENT", "GOOD", "NEEDS_ATTENTION", "CRITICAL"
  topIndexingIssues: string[]; // Main issues preventing indexing
  recommendations: IndexingRecommendation[];
  
  // Timeline data for charts
  last30Days: DailyIndexingData[];
}

export interface IndexingRecommendation {
  title: string;
  description: string;
  priority: string; // "HIGH", "MEDIUM", "LOW"
  actionType: string; // "TECHNICAL_FIX", "CONTENT_IMPROVEMENT", "SUBMISSION"
  estimatedImpact: number; // Estimated pages that could be fixed
}

export interface DailyIndexingData {
  date: string;
  newIndexed: number;
  newSubmitted: number;
  errors: number;
  totalIndexed: number;
}
